import { ClickAwayListener, Fade, FormControlLabel, Grid, Popper, Typography } from "@mui/material";
import CatalogCheckbox from "../../EnhancedCheckbox";
import React, { useEffect, useRef } from "react";
import { styled } from '@mui/material/styles';

interface ColumnOption {
  id: string;
  label: string;
  hidden?: boolean;
  toggleable?: boolean;
}

interface TableExtensions {
  isColumnHidden: (column: ColumnOption) => boolean;
  toggleColumn: (column: ColumnOption, hide: boolean) => void;
}

interface ColumnsPopperProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  config: { columns: ColumnOption[] };
  table: { extensions: TableExtensions };
}

const FiltersCategories = styled(Grid)(() => ({
  width: "250px",
  margin: "16px 0 0 0",
  background: "#FFFFFF",
  border: "1px solid #DCDCDC",
  borderRadius: "6px",
}));

const Header = styled(Grid)(() => ({
  padding: "8px",
  borderBottom: "1px solid #DCDCDC",
  width: "100%"
}));

const ColumnsContainer = styled(Grid)(() => ({
  maxHeight: "400px",
  overflowY: "auto",
  padding: "8px",
  width: "100%"
}));

export default function ColumnsPopper(props: ColumnsPopperProps) {
  const { open, anchorEl, setOpen, setAnchorEl, config, table } = props;

  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    // Focus the first row checkbox when the popper opens. Defer one tick so the
    // Fade transition has mounted the dialog content into the DOM.
    const id = window.setTimeout(() => {
      const first = dialogRef.current?.querySelector<HTMLInputElement>('input[type="checkbox"]');
      first?.focus();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  return (
    <Popper open={open} anchorEl={anchorEl} transition placement={"bottom-end"}>
      {({ TransitionProps }) => (
        <ClickAwayListener onClickAway={() => {
          setOpen(false);
          setAnchorEl(null);
        }}>
          <Fade {...TransitionProps} timeout={350}>
            <div ref={dialogRef} role="dialog" aria-label="Columns" aria-modal="false">
              <FiltersCategories container>
                <Header item size={12}>
                  <Typography><b>Columns</b></Typography>
                </Header>
                <ColumnsContainer item size={12}>
                  {config.columns.map((column, index) => {
                    if (column.hidden && !column.toggleable) return null;
                    if (!column.toggleable) return null;
                    return (
                      <Grid key={index} item container justifyContent={"flex-start"}
                        alignItems={"center"}
                        style={{ marginBottom: "8px" }}>
                        <FormControlLabel
                          control={
                            <CatalogCheckbox
                              checked={!table.extensions.isColumnHidden(column)}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                table.extensions.toggleColumn(column, !e.target.checked)
                              }
                            />
                          }
                          label={
                            <Typography style={{
                              fontSize: "14px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "150px",
                              whiteSpace: "nowrap"
                            }}>
                              {column.label}
                            </Typography>
                          }
                          sx={{ m: 0, gap: "10px" }}
                        />
                      </Grid>
                    );
                  })}
                </ColumnsContainer>
              </FiltersCategories>
            </div>
          </Fade>
        </ClickAwayListener>
      )}
    </Popper>
  );
}
